import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import type { MatrizArvoreNo, MatrizContaContabil, MatrizLogEntry, MatrizUsuarioApto } from './models';
import workflowService from './workflowService';
import CampoJustificativaSox from './CampoJustificativaSox';
import { useJustificativaSugerida } from './useJustificativaSugerida';
import type { JustificativaSugerida } from './useJustificativaSugerida';
import { justificativaVinculoResponsavel } from './justificativaSox';
import { formatarCnpj } from '../../utils/String';

// ─── TreePanel ────────────────────────────────────────────────────────────────

const TreePanel: React.FC<{
  arvore: MatrizArvoreNo[];
  expandidos: Set<string>;
  selecionadoId: string | null;
  gruposContas: string[];          // grupos do plano de contas da empresa selecionada
  grupoFiltro: string | null;      // grupo de conta atualmente filtrado
  onToggle: (id: string) => void;
  onSelect: (no: MatrizArvoreNo) => void;
  onSelectGrupo: (grupo: string | null) => void;
}> = ({ arvore, expandidos, selecionadoId, gruposContas, grupoFiltro, onToggle, onSelect, onSelectGrupo }) => {
  const grupos = arvore.filter(n => n.tipo === 'GRUPO');
  const semGrupo = arvore.filter(n => n.tipo === 'EMPRESA' && !n.paiId);

  const renderEmpresa = (no: MatrizArvoreNo) => {
    const isSelected = selecionadoId === no.id;
    return (
      <div key={no.id}>
        <div
          className={`wf-tree-empresa ${isSelected ? 'wf-tree-empresa--ativa' : ''}`}
          onClick={() => onSelect(no)}
          title={no.cnpj ?? undefined}
        >
          <span style={{ marginRight: 6 }}>🏢</span>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{no.nome}</div>
            {no.cnpj && <div style={{ fontSize: 11, color: '#a1a5b7' }}>{formatarCnpj(no.cnpj)}</div>}
          </div>
        </div>
        {/* Grupo de contas — seletor direto em vez de lista rolável, mais rápido pra achar
            o grupo certo quando há muitos grupos ou a árvore já está grande. */}
        {isSelected && gruposContas.length > 0 && (
          <div style={{ padding: '4px 8px 8px 28px' }}>
            <div style={{ fontSize: 11, color: '#a1a5b7', fontWeight: 600, letterSpacing: '0.4px', marginBottom: 4 }}>
              GRUPO DE CONTAS
            </div>
            <select
              value={grupoFiltro ?? ''}
              onClick={e => e.stopPropagation()}
              onChange={e => onSelectGrupo(e.target.value || null)}
              style={{ width: '100%', fontSize: 12, padding: '5px 6px', borderRadius: 6, border: '1px solid #dfe3ea', color: '#5e6278' }}
            >
              <option value=''>Todos os grupos</option>
              {gruposContas.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        )}
      </div>
    );
  };

  const renderGrupo = (g: MatrizArvoreNo) => {
    const filhos = arvore.filter(n => n.paiId === g.id);
    const aberto = expandidos.has(g.id);
    return (
      <div key={g.id}>
        <div className='wf-tree-grupo' onClick={() => onToggle(g.id)}>
          <span style={{ marginRight: 6, fontSize: 11 }}>{aberto ? '▼' : '▶'}</span>
          <span style={{ marginRight: 6 }}>🏛️</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{g.nome}</div>
            <div style={{ fontSize: 11, color: '#a1a5b7' }}>{g.filhosCount} empresa(s)</div>
          </div>
        </div>
        {aberto && <div style={{ paddingLeft: 16 }}>{filhos.map(renderEmpresa)}</div>}
      </div>
    );
  };

  return (
    <div className='wf-card' style={{ minWidth: 260, maxWidth: 280, flexShrink: 0, padding: 0 }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f1f4', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🏗️</span> Hierarquia
      </div>
      <div style={{ padding: 8, overflowY: 'auto', maxHeight: 'calc(100vh - 260px)' }}>
        {grupos.map(renderGrupo)}
        {semGrupo.map(renderEmpresa)}
        {arvore.length === 0 && <p style={{ padding: '12px 8px', color: '#a1a5b7', fontSize: 13 }}>Nenhum grupo/empresa cadastrado.</p>}
      </div>
    </div>
  );
};

// ─── StatusBadge ─────────────────────────────────────────────────────────────

const VinculoBadge: React.FC<{ status: MatrizContaContabil['statusVinculo'] }> = ({ status }) => {
  const map: Record<string, [string, string]> = {
    PENDENTE: ['wf-badge wf-badge--aguardando_aprovacao', 'Pendente de vínculo'],
    VINCULADO: ['wf-badge wf-badge--aprovada', 'Vinculado'],
    BLOQUEADO: ['wf-badge wf-badge--bloqueada', '🔒 Bloqueado'],
  };
  const [cls, label] = map[status] ?? ['wf-badge', status];
  return <span className={cls}>{label}</span>;
};

// ─── ApproverPanel (painel lateral direito — substitui modal) ─────────────────

const ApproverPanel: React.FC<{
  total: number;
  usuarios: MatrizUsuarioApto[];
  cargos: { cargoId: number; nomeCargo: string; nivelHierarquico: number }[];
  carregando: boolean;
  buscaUsuario: string;
  onBuscaUsuarioChange: (v: string) => void;
  modo: 'grupo' | 'usuario';
  onModoChange: (m: 'grupo' | 'usuario') => void;
  cargoId: number | null;
  aprovadorId: number | null;
  justificativa: JustificativaSugerida;
  salvando: boolean;
  onSelectCargo: (id: number) => void;
  onSelect: (id: number) => void;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ total, usuarios, cargos, carregando, buscaUsuario, onBuscaUsuarioChange, modo, onModoChange, cargoId, aprovadorId, justificativa, salvando, onSelectCargo, onSelect, onConfirm, onClose }) => (
  <div style={{
    width: 320, flexShrink: 0,
    display: 'flex', flexDirection: 'column',
    background: '#fff', borderRadius: 12,
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
    border: '1px solid #e8edf3',
    maxHeight: 'calc(100vh - 180px)',
    position: 'sticky', top: 20,
  }}>
    {/* Header */}
    <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f1f4', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <h3 style={{ margin: 0, fontSize: 15 }}>Designar Responsável SOX</h3>
        <p style={{ margin: '3px 0 0', fontSize: 12, color: '#7e8299' }}>{total} conta(s) selecionada(s)</p>
      </div>
      <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#a1a5b7', padding: 4 }} onClick={onClose}>✕</button>
    </div>

    {/* Toggle Grupo/Cargo × Usuário específico */}
    <div style={{ display: 'flex', gap: 6, padding: '10px 20px 0' }}>
      <button
        className={`wf-btn wf-btn--sm ${modo === 'grupo' ? 'wf-btn--primary' : 'wf-btn--secondary'}`}
        style={{ flex: 1 }}
        onClick={() => onModoChange('grupo')}
      >
        Grupo/Cargo
      </button>
      <button
        className={`wf-btn wf-btn--sm ${modo === 'usuario' ? 'wf-btn--primary' : 'wf-btn--secondary'}`}
        style={{ flex: 1 }}
        onClick={() => onModoChange('usuario')}
      >
        Usuário específico
      </button>
    </div>

    {/* Banner SoD */}
    <div style={{ padding: '10px 20px', background: '#f8f9fa', borderBottom: '1px solid #f1f1f4', fontSize: 11, color: '#7e8299' }}>
      {modo === 'grupo'
        ? <><strong style={{ color: '#3f4254' }}>Responsabilidade por grupo:</strong> todos os usuários ativos do cargo escolhido recebem a responsabilidade desta conta.</>
        : <><strong style={{ color: '#3f4254' }}>Validação SoD ativa:</strong> Apenas usuários N2+ são elegíveis como aprovadores (política SOX).</>}
    </div>

    {modo === 'grupo' ? (
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px' }}>
        {cargos.length === 0
          ? <p style={{ textAlign: 'center', color: '#a1a5b7', padding: 20, fontSize: 12 }}>Nenhum cargo cadastrado. Cadastre cargos primeiro.</p>
          : cargos.map(c => (
            <div
              key={c.cargoId}
              onClick={() => onSelectCargo(c.cargoId)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                borderRadius: 8, marginBottom: 5, cursor: 'pointer',
                border: cargoId === c.cargoId ? '2px solid #009ef7' : '1.5px solid #f1f1f4',
                background: cargoId === c.cargoId ? '#e8f4ff' : '#fff',
              }}
            >
              <span className='wf-nivel-badge'>N{c.nivelHierarquico}</span>
              <div style={{ flex: 1, fontWeight: 600, fontSize: 13 }}>{c.nomeCargo}</div>
              <span style={{ color: cargoId === c.cargoId ? '#009ef7' : '#d1d5db', fontSize: 16 }}>
                {cargoId === c.cargoId ? '●' : '○'}
              </span>
            </div>
          ))
        }
      </div>
    ) : (
    <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px' }}>
      <input
        className='wf-input'
        style={{ width: '100%', fontSize: 12, marginBottom: 8 }}
        placeholder='Filtrar por nome, cargo ou nível (ex.: N3)...'
        value={buscaUsuario}
        onChange={e => onBuscaUsuarioChange(e.target.value)}
      />
      {carregando
        ? <p style={{ textAlign: 'center', color: '#a1a5b7', padding: 20 }}>Carregando usuários...</p>
        : usuarios
          .filter(u => {
            const q = buscaUsuario.trim().toLowerCase();
            if (!q) return true;
            return u.nome.toLowerCase().includes(q)
              || u.cargo.toLowerCase().includes(q)
              || `n${u.nivelHierarquico}` === q
              || (u.email ?? '').toLowerCase().includes(q);
          })
          .map(u => (
          <div
            key={u.userId}
            onClick={() => u.aptoSod && onSelect(u.userId)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              borderRadius: 8, marginBottom: 5, cursor: u.aptoSod ? 'pointer' : 'not-allowed',
              border: aprovadorId === u.userId ? '2px solid #009ef7' : '1.5px solid #f1f1f4',
              background: aprovadorId === u.userId ? '#e8f4ff' : u.aptoSod ? '#fff' : '#fafafa',
              opacity: u.aptoSod ? 1 : 0.55,
            }}
          >
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: u.aptoSod ? '#009ef7' : '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
              {u.nome.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.nome}</div>
              <div style={{ fontSize: 11, color: '#7e8299' }}>N{u.nivelHierarquico} · {u.cargo}</div>
              {!u.aptoSod && <div style={{ fontSize: 10, color: '#f1416c', marginTop: 1 }}>⛔ {u.motivoInaptidao}</div>}
            </div>
            {u.aptoSod && <span style={{ color: aprovadorId === u.userId ? '#009ef7' : '#d1d5db', fontSize: 16, flexShrink: 0 }}>
              {aprovadorId === u.userId ? '●' : '○'}
            </span>}
          </div>
        ))
      }
    </div>
    )}

    {/* Justificativa SOX */}
    <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f1f4' }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#3f4254', marginBottom: 4 }}>
        Justificativa SOX <span style={{ color: '#f1416c' }}>*</span>
      </label>
      <CampoJustificativaSox
        justificativa={justificativa}
        className='wf-input'
        rows={5}
        style={{ width: '100%', fontSize: 12, resize: 'none' }}
        placeholder='Descreva o motivo do vínculo...'
      />
      {justificativa.texto.trim().length < 5 && justificativa.texto.length > 0 && (
        <span style={{ fontSize: 11, color: '#f1416c' }}>
          Mínimo 5 caracteres ({5 - justificativa.texto.trim().length} restante{5 - justificativa.texto.trim().length !== 1 ? 's' : ''})
        </span>
      )}
    </div>

    {/* Ações */}
    <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f1f4', display: 'flex', gap: 8 }}>
      <button className='wf-btn wf-btn--secondary' style={{ flex: 1 }} onClick={onClose} disabled={salvando}>
        Cancelar
      </button>
      <button
        className='wf-btn wf-btn--primary'
        style={{ flex: 2 }}
        onClick={onConfirm}
        disabled={(modo === 'grupo' ? !cargoId : !aprovadorId) || justificativa.texto.trim().length < 5 || salvando}
      >
        {salvando ? 'Salvando...' : 'Confirmar Vínculo SOX'}
      </button>
    </div>
  </div>
);

// ─── ImportModal ──────────────────────────────────────────────────────────────

const ImportModal: React.FC<{
  tenantId: number;
  onClose: () => void;
  onSucesso: (count: number) => void;
}> = ({ tenantId, onClose, onSucesso }) => {
  const [csv, setCsv] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const handleImportar = async () => {
    if (!periodo.trim()) { setErro('Informe o período de referência (ex.: 06/2025).'); return; }
    const linhas = csv.trim().split('\n').filter(l => l.trim());
    if (linhas.length === 0) { setErro('Cole o conteúdo CSV das contas.'); return; }

    const contas = linhas.map(linha => {
      const [codigo, codigoReduzido, descricao, tipo, nivel, grupoConta, contaPaiCodigo] = linha.split(';').map(s => s.trim());
      return { codigo, codigoReduzido, descricao, tipo: tipo || 'ANALITICA', nivel: parseInt(nivel) || 1, grupoConta: grupoConta || undefined, contaPaiCodigo: contaPaiCodigo || undefined };
    }).filter(c => c.codigo && c.descricao);

    if (contas.length === 0) { setErro('Nenhuma conta válida encontrada. Formato: código;cód.reduzido;descrição;tipo;nível;grupo;pai'); return; }

    setSalvando(true);
    try {
      const { importadas } = await workflowService.matrizImportar({ tenantId, periodoReferencia: periodo, contas });
      onSucesso(importadas);
    } catch {
      setErro('Erro ao importar. Verifique o formato e tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 560, maxWidth: '95vw', padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <h3 style={{ margin: '0 0 4px' }}>Importar Plano de Contas</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#7e8299' }}>
          Cole o CSV exportado do ECD. Formato de cada linha:<br />
          <code style={{ fontSize: 11 }}>código;cód.reduzido;descrição;tipo;nível;grupo;conta_pai</code>
        </p>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Período de referência (MM/AAAA) *</span>
          <input
            className='wf-input'
            style={{ marginTop: 4, width: '100%' }}
            placeholder='ex.: 06/2025'
            value={periodo}
            onChange={e => setPeriodo(e.target.value)}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Conteúdo CSV *</span>
          <textarea
            className='wf-input'
            style={{ marginTop: 4, width: '100%', height: 160, fontFamily: 'monospace', fontSize: 12 }}
            placeholder={'1.1.01;111;Caixa e Equivalentes;ANALITICA;3;Ativo Circulante;\n1.1.02;112;Bancos Conta Corrente;ANALITICA;3;Ativo Circulante;'}
            value={csv}
            onChange={e => setCsv(e.target.value)}
          />
        </label>
        {erro && <p style={{ color: '#f1416c', fontSize: 13, marginBottom: 12 }}>{erro}</p>}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className='wf-btn wf-btn--secondary' onClick={onClose} disabled={salvando}>Cancelar</button>
          <button className='wf-btn wf-btn--primary' onClick={handleImportar} disabled={salvando}>
            {salvando ? 'Importando...' : 'Importar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MatrizResponsabilidadesPage ──────────────────────────────────────────────

const MatrizResponsabilidadesPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ fromOnboarding?: boolean; etapa?: number }>();
  const fromOnboarding = (location.state as any)?.fromOnboarding ?? false;
  const onboardingEtapa = (location.state as any)?.etapa ?? 3;
  const params = new URLSearchParams(location.search);
  const autoTenantId = params.get('tenantId') ? Number(params.get('tenantId')) : null;
  const highlightContaId = params.get('highlight') ? Number(params.get('highlight')) : null;

  const [arvore, setArvore] = useState<MatrizArvoreNo[]>([]);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [selecionado, setSelecionado] = useState<MatrizArvoreNo | null>(null);

  const [contas, setContas] = useState<MatrizContaContabil[]>([]);
  const [carregandoContas, setCarregandoContas] = useState(false);
  const [busca, setBusca] = useState('');
  const [grupoFiltro, setGrupoFiltro] = useState<string | null>(null);
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());
  // Visão de agrupamento: grupo do ECD (padrão) ou Grupos De/Para cadastrados na Etapa 1
  const [agruparPor, setAgruparPor] = useState<'ecd' | 'depara'>('ecd');
  const [deParaItens, setDeParaItens] = useState<{ grupoNome: string; subgrupoNome: string | null; contaId: number }[]>([]);
  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [destacada, setDestacada] = useState<number | null>(highlightContaId);

  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const highlightTimerRef = useRef<number | null>(null);

  // Painel lateral de aprovador (substitui modal)
  const [painelAberto, setPainelAberto] = useState(false);
  const [usuariosAptos, setUsuariosAptos] = useState<MatrizUsuarioApto[]>([]);
  const [cargos, setCargos] = useState<{ cargoId: number; nomeCargo: string; nivelHierarquico: number }[]>([]);
  const [carregandoUsuarios, setCarregandoUsuarios] = useState(false);
  const [vinculoModo, setVinculoModo] = useState<'grupo' | 'usuario'>('grupo');
  const [cargoSelecionadoId, setCargoSelecionadoId] = useState<number | null>(null);
  const [aprovadorId, setAprovadorId] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);
  // Justificativa SOX já sugerida (nº de contas + cargo/usuário escolhido); a pessoa revisa e edita se quiser.
  const justificativaSox = useJustificativaSugerida(
    painelAberto
      ? justificativaVinculoResponsavel({
          qtdContas: selecionadas.size,
          modo: vinculoModo,
          responsavel: vinculoModo === 'grupo'
            ? cargos.find(c => c.cargoId === cargoSelecionadoId)?.nomeCargo
            : usuariosAptos.find(u => u.userId === aprovadorId)?.nome,
        })
      : '');

  const [log, setLog] = useState<MatrizLogEntry[]>([]);
  const [logAberto, setLogAberto] = useState(false);
  const [importModalAberto, setImportModalAberto] = useState(false);

  useEffect(() => {
    workflowService.listarCargos().then(c => setCargos(c.filter(x => x.ativo))).catch(() => {});
    workflowService.matrizArvore().then(data => {
      setArvore(data);
      if (autoTenantId) {
        const noEmpresa = data.find(n => n.tipo === 'EMPRESA' && n.tenantId === autoTenantId);
        if (noEmpresa) {
          if (noEmpresa.paiId) setExpandidos(new Set([noEmpresa.paiId]));
          setSelecionado(noEmpresa);
          setCarregandoContas(true);
          workflowService.matrizContas(autoTenantId)
            .then(setContas)
            .catch(() => setContas([]))
            .finally(() => setCarregandoContas(false));
          workflowService.deParaListar(noEmpresa.empresaId ?? autoTenantId)
            .then(setDeParaItens)
            .catch(() => setDeParaItens([]));
        }
      } else {
        const primeiroGrupo = data.find(n => n.tipo === 'GRUPO');
        if (primeiroGrupo) setExpandidos(new Set([primeiroGrupo.id]));
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!destacada || contas.length === 0) return;
    const ref = rowRefs.current.get(destacada);
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = window.setTimeout(() => setDestacada(null), 4000);
    }
  }, [destacada, contas]);

  const handleToggleGrupo = useCallback((id: string) => {
    setExpandidos(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelectEmpresa = useCallback((no: MatrizArvoreNo) => {
    setSelecionado(no);
    setSelecionadas(new Set());
    setBusca('');
    setGrupoFiltro(null);
    setLog([]);
    setLogAberto(false);
    setPainelAberto(false);
    setDeParaItens([]);
    if (!no.tenantId) return;
    setCarregandoContas(true);
    workflowService.matrizContas(no.tenantId).then(setContas).catch(() => setContas([])).finally(() => setCarregandoContas(false));
    workflowService.deParaListar(no.empresaId ?? no.tenantId).then(setDeParaItens).catch(() => setDeParaItens([]));
  }, []);

  // Grupos de conta distintos das contas carregadas (para a árvore de 3 níveis)
  const gruposContas = useMemo(() => {
    const set = new Set<string>();
    contas.forEach(c => { if (c.grupoConta?.trim()) set.add(c.grupoConta.trim()); });
    return Array.from(set).sort();
  }, [contas]);

  const contasFiltradas = useMemo(() => {
    let lista = contas;
    if (grupoFiltro) lista = lista.filter(c => (c.grupoConta?.trim() || 'Sem grupo') === grupoFiltro);
    if (!busca.trim()) return lista;
    const b = busca.toLowerCase();
    return lista.filter(c =>
      c.codigo.toLowerCase().includes(b)
      || c.codigoReduzido.toLowerCase().includes(b)
      || c.descricao.toLowerCase().includes(b)
      || (c.grupoConta ?? '').toLowerCase().includes(b)
    );
  }, [contas, busca, grupoFiltro]);

  // contaId → Grupo De/Para (nome + descrição/subgrupo)
  const deParaPorConta = useMemo(() => {
    const m = new Map<number, { grupoNome: string; subgrupoNome: string | null }>();
    deParaItens.forEach(i => m.set(i.contaId, { grupoNome: i.grupoNome, subgrupoNome: i.subgrupoNome }));
    return m;
  }, [deParaItens]);

  const contasAgrupadas = useMemo((): [string, MatrizContaContabil[]][] => {
    const groups = new Map<string, MatrizContaContabil[]>();
    contasFiltradas.forEach(c => {
      const g = agruparPor === 'depara'
        ? (deParaPorConta.get(c.contaId)?.grupoNome ?? 'Sem Grupo De/Para')
        : (c.grupoConta?.trim() || 'Sem grupo');
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)!.push(c);
    });
    return Array.from(groups.entries());
  }, [contasFiltradas, agruparPor, deParaPorConta]);

  const contasSemReferencial = useMemo(() => contas.filter(c => c.semReferencial), [contas]);

  const totalSelecionadas = selecionadas.size;
  const todasSelecionadas = contasFiltradas.length > 0 && contasFiltradas.every(c => selecionadas.has(c.contaId));

  const handleToggleConta = (contaId: number) => {
    setSelecionadas(prev => {
      const next = new Set(prev);
      next.has(contaId) ? next.delete(contaId) : next.add(contaId);
      return next;
    });
  };

  const handleToggleTodos = () => {
    if (todasSelecionadas) {
      setSelecionadas(new Set());
    } else {
      setSelecionadas(new Set(contasFiltradas.filter(c => !c.bloqueada).map(c => c.contaId)));
    }
  };

  const handleToggleGrupoContas = (contasDoGrupo: MatrizContaContabil[]) => {
    const selecionaveis = contasDoGrupo.filter(c => !c.bloqueada);
    const todasDoGrupo = selecionaveis.length > 0 && selecionaveis.every(c => selecionadas.has(c.contaId));
    setSelecionadas(prev => {
      const next = new Set(prev);
      selecionaveis.forEach(c => { todasDoGrupo ? next.delete(c.contaId) : next.add(c.contaId); });
      return next;
    });
  };

  const abrirPainel = async (contaIds?: number[]) => {
    if (contaIds) setSelecionadas(new Set(contaIds));
    setAprovadorId(null);
    setCargoSelecionadoId(null);
    setBuscaUsuario('');
    setVinculoModo('grupo');
    justificativaSox.restaurar();
    setPainelAberto(true);
    if (!selecionado?.tenantId) return;
    setCarregandoUsuarios(true);
    workflowService.matrizUsuariosAptos(selecionado.tenantId)
      .then(setUsuariosAptos)
      .catch(() => setUsuariosAptos([]))
      .finally(() => setCarregandoUsuarios(false));
  };

  const handleConfirmarVinculo = async () => {
    if (!selecionado?.tenantId) return;
    if (vinculoModo === 'grupo' && !cargoSelecionadoId) return;
    if (vinculoModo === 'usuario' && !aprovadorId) return;
    setSalvando(true);
    try {
      const resultado = await workflowService.matrizVincular({
        tenantId: selecionado.tenantId,
        contaIds: Array.from(selecionadas),
        aprovadorUserId: vinculoModo === 'usuario' ? aprovadorId! : undefined,
        cargoId: vinculoModo === 'grupo' ? cargoSelecionadoId! : undefined,
        usuarioLogadoId: 0,
        justificativaSox: justificativaSox.texto.trim(),
      });
      setPainelAberto(false);
      setSelecionadas(new Set());
      const atualizadas = await workflowService.matrizContas(selecionado.tenantId);
      setContas(atualizadas);
      Swal.fire({
        icon: 'success',
        title: 'Vínculo atualizado com sucesso!',
        html: `<b>${resultado.vinculadas}</b> conta(s) vinculada(s). Logs de auditoria gerados.${resultado.bloqueadas > 0 ? `<br/><small style="color:#e55353">${resultado.bloqueadas} bloqueada(s) — período fechado.</small>` : ''}`,
        timer: 4000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch {
      Swal.fire({ icon: 'error', title: 'Erro ao salvar vínculo', text: 'Tente novamente.', confirmButtonText: 'OK' });
    } finally {
      setSalvando(false);
    }
  };

  const handleCarregarLog = async () => {
    if (!selecionado?.tenantId) return;
    const entries = await workflowService.matrizLog(selecionado.tenantId).catch(() => []);
    setLog(entries);
    setLogAberto(true);
  };

  const stats = useMemo(() => {
    const total = contas.length;
    const vinculadas = contas.filter(c => c.statusVinculo === 'VINCULADO').length;
    const pendentes = contas.filter(c => c.statusVinculo === 'PENDENTE').length;
    const bloqueadas = contas.filter(c => c.statusVinculo === 'BLOQUEADO').length;
    return { total, vinculadas, pendentes, bloqueadas };
  }, [contas]);

  return (
    <div className='wf-page'>
      <div className='wf-header'>
        <div>
          <h1>Matriz de Responsabilidades SOX</h1>
          <p>Vincule contas contábeis a aprovadores com validação de segregação de funções (SoD).</p>
        </div>
        {fromOnboarding && (
          <div className='wf-header__actions'>
            <button
              className='wf-btn wf-btn--secondary'
              onClick={() => history.push('/workflow-aprovacao/onboarding', { goToEtapa: onboardingEtapa })}
            >
              ← Onboarding Etapa {onboardingEtapa}
            </button>
          </div>
        )}
      </div>

      {/* Layout 3 colunas: Árvore | Contas | Painel Aprovador */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* Painel Esquerdo — Árvore hierárquica (Grupo > Empresa > Grupos de Contas) */}
        <TreePanel
          arvore={arvore}
          expandidos={expandidos}
          selecionadoId={selecionado?.id ?? null}
          gruposContas={gruposContas}
          grupoFiltro={grupoFiltro}
          onToggle={handleToggleGrupo}
          onSelect={handleSelectEmpresa}
          onSelectGrupo={setGrupoFiltro}
        />

        {/* Painel Central — Contas */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!selecionado ? (
            <section className='wf-card' style={{ textAlign: 'center', padding: '48px 24px', color: '#a1a5b7' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🏢</div>
              <strong style={{ fontSize: 15, color: '#5e6278' }}>Selecione uma empresa na hierarquia</strong>
              <p style={{ margin: '8px 0 0', fontSize: 13 }}>O plano de contas e os vínculos de aprovadores serão exibidos aqui.</p>
            </section>
          ) : (
            <>
              {/* Header da empresa */}
              <section className='wf-card' style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <h2 style={{ margin: 0 }}>{selecionado.nome}</h2>
                    {selecionado.cnpj && <p style={{ margin: '2px 0 0', fontSize: 13, color: '#7e8299' }}>CNPJ: {formatarCnpj(selecionado.cnpj)}</p>}
                    {grupoFiltro && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        <span className='wf-badge wf-badge--aprovada' style={{ fontSize: 11 }}>📂 {grupoFiltro}</span>
                        <button className='wf-link' style={{ fontSize: 12 }} onClick={() => setGrupoFiltro(null)}>✕ limpar filtro</button>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => setImportModalAberto(true)}>+ Importar Contas</button>
                    <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={handleCarregarLog}>📋 Ver Log SOX</button>
                  </div>
                </div>

                {contas.length > 0 && (
                  <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
                    {[
                      ['Total de contas', stats.total, 'primary'],
                      ['Vinculadas', stats.vinculadas, 'success'],
                      ['Pendentes de vínculo', stats.pendentes, 'warning'],
                      ['Bloqueadas', stats.bloqueadas, 'danger'],
                    ].map(([label, value, color]) => (
                      <div key={label as string} className={`wf-metric wf-metric--${color}`} style={{ flex: '1 1 120px', minWidth: 100 }}>
                        <span>{label}</span><strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Aviso: contas sem referencial contábil */}
              {contasSemReferencial.length > 0 && (
                <section className='wf-card' style={{ marginBottom: 16, borderLeft: '4px solid #f1416c', background: '#fff5f8' }}>
                  <strong style={{ color: '#f1416c' }}>
                    Atenção: {contasSemReferencial.length} conta(s) sem referencial cadastrado
                  </strong>
                  <p style={{ margin: '4px 0 8px', fontSize: 12, color: '#5e6278' }}>
                    Cadastre o referencial para prosseguir com o Workflow — conciliações dessas contas não avançam de etapa.
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, maxHeight: 110, overflowY: 'auto' }}>
                    {contasSemReferencial.map(c => (
                      <li key={c.contaId}>
                        A conta contábil <strong>{c.codigo} – {c.descricao}</strong> não possui referencial cadastrado.
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Barra de busca + ações em massa */}
              <section className='wf-card' style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    className='wf-input'
                    style={{ flex: 1, minWidth: 200 }}
                    placeholder='Buscar por código, código reduzido ou descrição...'
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                  />
                  <select
                    className='wf-input'
                    value={agruparPor}
                    onChange={e => { setAgruparPor(e.target.value as 'ecd' | 'depara'); setSelecionadas(new Set()); }}
                    title='Como agrupar as contas'
                  >
                    <option value='ecd'>Agrupar por: Grupo do ECD</option>
                    <option value='depara'>Agrupar por: Grupo De/Para</option>
                  </select>
                  {totalSelecionadas > 0 && (
                    <span style={{ fontSize: 13, color: '#009ef7', fontWeight: 600 }}>
                      {totalSelecionadas} selecionada(s)
                    </span>
                  )}
                  <button
                    className='wf-btn wf-btn--primary'
                    disabled={totalSelecionadas === 0}
                    onClick={() => abrirPainel()}
                  >
                    👤 Designar Aprovador
                  </button>
                </div>
              </section>

              {/* Tabela de Contas */}
              <section className='wf-card'>
                {carregandoContas ? (
                  <p style={{ textAlign: 'center', padding: 32, color: '#a1a5b7' }}>Carregando plano de contas...</p>
                ) : contasFiltradas.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 32, color: '#a1a5b7' }}>
                    {contas.length === 0
                      ? <><div style={{ fontSize: 36 }}>📂</div><p>Nenhuma conta importada para esta empresa.<br /><small>Use o botão "Importar Contas" para carregar o plano de contas do ECD.</small></p></>
                      : <p>Nenhuma conta encontrada para "{busca}".</p>
                    }
                  </div>
                ) : (
                  <div className='wf-table-wrap' style={{ overflowX: 'auto' }}>
                    <table className='wf-table'>
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>
                            <input type='checkbox' checked={todasSelecionadas} onChange={handleToggleTodos} title='Selecionar todas' />
                          </th>
                          <th>Código</th>
                          <th>Descrição</th>
                          <th>Tipo</th>
                          <th>Aprovador Atual</th>
                          <th>Status</th>
                          <th style={{ width: 80 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {contasAgrupadas.map(([grupo, contasGrupo]) => {
                          const selecionaveisGrupo = contasGrupo.filter(c => !c.bloqueada);
                          const todasGrupoSel = selecionaveisGrupo.length > 0 && selecionaveisGrupo.every(c => selecionadas.has(c.contaId));
                          const pendentesGrupo = contasGrupo.filter(c => c.statusVinculo === 'PENDENTE').length;
                          return (
                            <React.Fragment key={grupo}>
                              <tr style={{ background: '#f5f8ff', borderTop: '2px solid #dfe3ea', borderBottom: '1px solid #e8edf3' }}>
                                <td style={{ padding: '8px 10px' }}>
                                  <input
                                    type='checkbox'
                                    checked={todasGrupoSel}
                                    onChange={() => handleToggleGrupoContas(contasGrupo)}
                                    title={`Selecionar todas de "${grupo}"`}
                                  />
                                </td>
                                <td colSpan={6} style={{ padding: '8px 10px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 12 }}>📁</span>
                                    <strong style={{ fontSize: 13, color: '#3f4254' }}>{grupo}</strong>
                                    {agruparPor === 'depara' && deParaPorConta.get(contasGrupo[0].contaId)?.subgrupoNome && (
                                      <span style={{ fontSize: 12, color: '#7e8299' }}>
                                        {deParaPorConta.get(contasGrupo[0].contaId)!.subgrupoNome}
                                      </span>
                                    )}
                                    <span style={{ fontSize: 11, color: '#7e8299' }}>{contasGrupo.length} conta(s)</span>
                                    {agruparPor === 'depara' && (() => {
                                      const chaves = new Set(contasGrupo.map(c => c.nomeCargo ? `g:${c.nomeCargo}` : c.aprovadorNome ? `u:${c.aprovadorNome}` : ''));
                                      if (chaves.size !== 1) return null;
                                      const k = Array.from(chaves)[0];
                                      return k ? <span className='wf-badge wf-badge--aprovada'>Responsável: {k.slice(2)}</span> : null;
                                    })()}
                                    {pendentesGrupo > 0 && (
                                      <span className='wf-badge wf-badge--aguardando_aprovacao'>{pendentesGrupo} pendente(s)</span>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                                  <button
                                    className='wf-btn wf-btn--primary wf-btn--sm'
                                    onClick={() => abrirPainel(selecionaveisGrupo.map(c => c.contaId))}
                                    disabled={selecionaveisGrupo.length === 0}
                                    title='Designar mesmo aprovador para todo o grupo'
                                  >
                                    Designar grupo
                                  </button>
                                </td>
                              </tr>
                              {contasGrupo.map(c => (
                                <tr
                                  key={c.contaId}
                                  ref={el => { if (el) rowRefs.current.set(c.contaId, el); else rowRefs.current.delete(c.contaId); }}
                                  className={destacada === c.contaId ? 'wf-row--destacada' : selecionadas.has(c.contaId) ? 'wf-row--selecionada' : ''}
                                >
                                  <td>
                                    <input
                                      type='checkbox'
                                      checked={selecionadas.has(c.contaId)}
                                      disabled={c.bloqueada}
                                      onChange={() => handleToggleConta(c.contaId)}
                                    />
                                  </td>
                                  <td>
                                    <strong style={{ fontSize: 13 }}>{c.codigo}</strong>
                                    {c.codigoReduzido && <span style={{ display: 'block', fontSize: 11, color: '#a1a5b7' }}>{c.codigoReduzido}</span>}
                                    {c.bloqueada && <span style={{ fontSize: 11, marginLeft: 4 }}>🔒</span>}
                                    {c.semReferencial && (
                                      <span className='wf-badge wf-badge--bloqueada' style={{ display: 'block', fontSize: 10, marginTop: 2 }}>
                                        Sem referencial
                                      </span>
                                    )}
                                  </td>
                                  <td style={{ maxWidth: 220 }}>
                                    <span style={{ fontSize: 13, paddingLeft: (c.nivel - 1) * 12 }}>{c.descricao}</span>
                                  </td>
                                  <td>
                                    <span style={{ fontSize: 11, background: c.tipo === 'SINTETICA' ? '#f5f8ff' : '#f0fff4', color: c.tipo === 'SINTETICA' ? '#3f6ad8' : '#107c10', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                                      {c.tipo}
                                    </span>
                                  </td>
                                  <td>
                                    {c.nomeCargo ? (
                                      <button
                                        className='wf-link'
                                        style={{ fontSize: 13 }}
                                        title='Clique para alterar responsável'
                                        onClick={() => !c.bloqueada && abrirPainel([c.contaId])}
                                      >
                                        🏷️ Grupo: {c.nomeCargo} ✎
                                      </button>
                                    ) : c.aprovadorNome ? (
                                      <button
                                        className='wf-link'
                                        style={{ fontSize: 13 }}
                                        title='Clique para alterar aprovador'
                                        onClick={() => !c.bloqueada && abrirPainel([c.contaId])}
                                      >
                                        {c.aprovadorNome} ✎
                                      </button>
                                    ) : (
                                      <span style={{ color: '#a1a5b7', fontSize: 12 }}>—</span>
                                    )}
                                  </td>
                                  <td><VinculoBadge status={c.statusVinculo} /></td>
                                  <td>
                                    {!c.bloqueada && (
                                      <button
                                        className='wf-btn wf-btn--primary wf-btn--sm'
                                        onClick={() => abrirPainel([c.contaId])}
                                        title='Vincular aprovador'
                                      >
                                        Vincular
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Log de Auditoria */}
              {logAberto && (
                <section className='wf-card' style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 14 }}>📋 Log de Auditoria SOX — Vínculos</h3>
                    <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => setLogAberto(false)}>✕ Fechar</button>
                  </div>
                  {log.length === 0 ? (
                    <p style={{ color: '#a1a5b7', fontSize: 13 }}>Nenhum vínculo registrado para esta empresa.</p>
                  ) : (
                    <div className='wf-table-wrap'>
                      <table className='wf-table'>
                        <thead>
                          <tr>
                            <th>Data/Hora</th>
                            <th>Ação</th>
                            <th>Conta</th>
                            <th>Aprovador Anterior</th>
                            <th>Aprovador Novo</th>
                            <th>Usuário</th>
                            <th>Justificativa</th>
                            <th>Hash SOX</th>
                          </tr>
                        </thead>
                        <tbody>
                          {log.map(l => (
                            <tr key={l.logId}>
                              <td style={{ fontSize: 12 }}>{new Date(l.acaoEm).toLocaleString('pt-BR')}</td>
                              <td>
                                <span style={{ fontSize: 11, background: '#f5f8ff', color: '#3f6ad8', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                                  {l.acao}
                                </span>
                              </td>
                              <td><strong>{l.contaCodigo}</strong></td>
                              <td style={{ fontSize: 12, color: '#7e8299' }}>{l.aprovadorAnteriorNome ?? '—'}</td>
                              <td style={{ fontWeight: 600, fontSize: 13, color: '#50cd89' }}>{l.aprovadorNovoNome}</td>
                              <td style={{ fontSize: 12 }}>{l.usuarioLogadoNome}</td>
                              <td style={{ fontSize: 12, color: '#5e6278', maxWidth: 180 }} title={l.justificativaSox ?? undefined}>
                                {l.justificativaSox ? (
                                  <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    {l.justificativaSox}
                                  </span>
                                ) : <span style={{ color: '#a1a5b7' }}>—</span>}
                              </td>
                              <td style={{ fontFamily: 'monospace', fontSize: 10, color: '#a1a5b7' }} title={l.hashValidacao}>{l.hashValidacao.substring(0, 12)}…</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        {/* Painel Direito — Aprovador (3° coluna, aparece quando painel está aberto) */}
        {painelAberto && (
          <ApproverPanel
            total={selecionadas.size}
            usuarios={usuariosAptos}
            cargos={cargos}
            carregando={carregandoUsuarios}
            buscaUsuario={buscaUsuario}
            onBuscaUsuarioChange={setBuscaUsuario}
            modo={vinculoModo}
            onModoChange={setVinculoModo}
            cargoId={cargoSelecionadoId}
            aprovadorId={aprovadorId}
            justificativa={justificativaSox}
            salvando={salvando}
            onSelectCargo={setCargoSelecionadoId}
            onSelect={setAprovadorId}
            onConfirm={handleConfirmarVinculo}
            onClose={() => setPainelAberto(false)}
          />
        )}
      </div>

      {/* Modal de Importação */}
      {importModalAberto && selecionado?.tenantId && (
        <ImportModal
          tenantId={selecionado.tenantId}
          onClose={() => setImportModalAberto(false)}
          onSucesso={count => {
            setImportModalAberto(false);
            Swal.fire({ icon: 'success', title: 'Importação concluída!', text: `${count} conta(s) novas importadas.`, timer: 3000, showConfirmButton: false, toast: true, position: 'top-end' });
            workflowService.matrizContas(selecionado.tenantId!).then(setContas).catch(() => {});
          }}
        />
      )}
    </div>
  );
};

export default MatrizResponsabilidadesPage;
